import {
  BerthResponseDto,
  CargoType,
  CarrierVisitDirection,
  JourneyCategory,
  OrderStatus,
  VesselVisitDto,
} from '@planning/app/api'
import { CarrierVisitViewStatus } from '@planning/constants'
import { VoyageDto } from '@planning/pages/VesselVisit/Stores/VesselVisitGeneralInformationFormStore'
import { isVisitStatusBlocked } from '@planning/utils/visit-utils'
import _ from 'lodash'
import { computed, makeObservable } from 'mobx'
import { EntityItem } from '../base/EntityItem'
import { IBerthItem } from '../berth/BerthItem'
import { ICompanyItem } from '../externalDriver/CompanyItem'
import { getCarrierVisitViewStatus } from '../helpers'
import { IOrderItem } from '../order/OrderItem'
import { RailVisitItem } from '../railVisit/RailVisitItem'
import { RailVisitItemStore } from '../railVisit/RailVisitItemStore'
import { IEntity, IEntityStore } from '../types'
import { IVesselItem } from '../vessel/VesselItem'
import { VesselVisitItemStore } from './VesselVisitItemStore'

export const calculateContainerOrderCounts = (orderItems: IOrderItem[]): IOrderCounts => {
  if (!orderItems.length)
    return {
      confirmed: 0,
      pending: 0,
      total: 0,
    }

  const orders = orderItems.map(o => o.data).filter(order => !order.commodityId)

  const total = orders.length
  const pending = orders.filter(order => order.status === OrderStatus.Open).length
  const confirmed = total - pending

  return {
    confirmed,
    pending,
    total,
  }
}

export const calculateGeneralCargoOrderCounts = (orderItems: IOrderItem[]): IOrderCounts => {
  if (!orderItems.length)
    return {
      confirmed: 0,
      pending: 0,
      total: 0,
    }

  const orders = orderItems.map(o => o.data).filter(order => order.commodityId)
  const total = orders.reduce((acc, order) => acc + (order.plannedCargoAmount || 0), 0)
  const pending = orders
    .filter(order => order.status === OrderStatus.Open)
    .reduce((acc, order) => acc + (order.plannedCargoAmount || 0), 0)
  const confirmed = total - pending

  return {
    confirmed,
    pending,
    total,
  }
}

export interface IOrderCounts {
  confirmed: number
  pending: number
  total: number
}

export interface IOrderCountsByCargoType {
  generalCargo: IOrderCounts
  container: IOrderCounts
  estimated: number
}

export interface IOrderCountsByDirection {
  discharge: IOrderCounts
  load: IOrderCounts
}

export interface IDirectionData {
  orders: IOrderItem[]
  containerOrders: IOrderItem[]
  generalCargoOrders: IOrderItem[]
  orderCounts: IOrderCountsByCargoType
  getCount: (countType: keyof IOrderCounts, cargoType: CargoType) => number
}

export class DirectionData implements IDirectionData {
  constructor(
    private direction: CarrierVisitDirection,
    private item: VesselVisitItem | RailVisitItem,
    private itemStore: VesselVisitItemStore | RailVisitItemStore,
  ) {
    makeObservable(this, {
      orders: computed,
      containerOrders: computed,
      generalCargoOrders: computed,
      orderCounts: computed,
    })
  }

  get orders() {
    return _.filter(
      this.item.orders,
      order =>
        order.data.direction === this.direction && order.journeyCategory !== JourneyCategory.Restow,
    )
  }

  get containerOrders() {
    return _.filter(this.orders, order => !!order.data.containerId)
  }

  get generalCargoOrders() {
    return _.filter(this.orders, order => !order.data.containerId && !!order.data.commodityId)
  }

  get orderCounts() {
    return {
      container: calculateContainerOrderCounts(this.orders),
      generalCargo: calculateGeneralCargoOrderCounts(this.orders),
      // [OCTA-948] TODO: Do we need estimates for GC?
      estimated:
        this.direction === CarrierVisitDirection.Inbound
          ? this.item.data.dischargeEstimate ?? 0
          : this.item.data.loadEstimate ?? 0,
    } as IOrderCountsByCargoType
  }

  getCount = (countType: keyof IOrderCounts, cargoType: CargoType) => {
    if (!this.orderCounts) return 0

    const cargoKey = cargoType === CargoType.GeneralCargo ? 'generalCargo' : 'container'

    if (!this.orderCounts[cargoKey]) return 0

    return this.orderCounts[cargoKey][countType]
  }
}

export interface IJourneyCategoryData {
  orders: IOrderItem[]
  dischargeOrders: IOrderItem[]
  loadOrders: IOrderItem[]
  orderCounts: IOrderCountsByDirection
  getCount: (countType: keyof IOrderCounts, direction: CarrierVisitDirection) => number
}

export class JourneyCategoryData implements IJourneyCategoryData {
  constructor(
    private readonly journeyCategory: JourneyCategory,
    private readonly visitItem: VesselVisitItem | RailVisitItem,
  ) {
    makeObservable(this, {
      orders: computed,
      dischargeOrders: computed,
      loadOrders: computed,
      orderCounts: computed,
    })
  }

  get orders() {
    return _.filter(this.visitItem.orders, order => order.journeyCategory === this.journeyCategory)
  }

  get dischargeOrders() {
    return _.filter(this.orders, order => order.data.direction === CarrierVisitDirection.Inbound)
  }

  get loadOrders() {
    return _.filter(this.orders, order => order.data.direction === CarrierVisitDirection.Outbound)
  }

  get orderCounts() {
    return {
      discharge: calculateContainerOrderCounts(this.dischargeOrders),
      load: calculateContainerOrderCounts(this.loadOrders),
    }
  }

  getCount = (countType: keyof IOrderCounts, direction: CarrierVisitDirection) => {
    if (!this.orderCounts) return 0

    const directionKey = direction === CarrierVisitDirection.Inbound ? 'discharge' : 'load'

    if (!this.orderCounts[directionKey]) return 0

    return this.orderCounts[directionKey][countType]
  }
}

export interface IVesselVisitItem extends IEntity<VesselVisitDto> {
  vessels: IVesselItem[]
  orders: IOrderItem[]
  berths: BerthResponseDto[]
  operator: ICompanyItem | undefined
  shippingAgent: ICompanyItem | undefined
  voyages: VoyageDto[]
  arrival: string | null | undefined
  departure: string | null | undefined
  load: IDirectionData //does not include restows
  discharge: IDirectionData //does not include restows
  restow: IJourneyCategoryData
  getCount: (
    countType: keyof IOrderCounts,
    direction: CarrierVisitDirection,
    cargoType: CargoType,
  ) => number
  status: CarrierVisitViewStatus
  hasOpenOrders: boolean
  inboundOpenOrders: IOrderItem[]
  outboundOpenOrders: IOrderItem[]
  restowOpenOrders: IOrderItem[]
  hasDraftConflict: boolean
  isVesselVisitBlockedForEdit: boolean
  isBerthed: boolean
}

export class VesselVisitItem extends EntityItem<VesselVisitDto> implements IVesselVisitItem {
  public readonly load: DirectionData
  public readonly discharge: DirectionData
  public readonly restow: JourneyCategoryData

  constructor(
    id: number,
    data: VesselVisitDto,
    private readonly itemStore: VesselVisitItemStore,
    private readonly vesselStore?: IEntityStore<IVesselItem>,
    private readonly berthStore?: IEntityStore<IBerthItem>,
    private readonly companyStore?: IEntityStore<ICompanyItem>,
  ) {
    super(id, data)
    makeObservable(this, {
      vessels: computed,
      orders: computed,
      berths: computed,
      operator: computed,
      shippingAgent: computed,
      voyages: computed,
      arrival: computed,
      departure: computed,
      status: computed,
      hasOpenOrders: computed,
      inboundOpenOrders: computed,
      outboundOpenOrders: computed,
      restowOpenOrders: computed,
      hasDraftConflict: computed,
      isVesselVisitBlockedForEdit: computed,
      isBerthed: computed,
    })

    this.load = new DirectionData(CarrierVisitDirection.Outbound, this, this.itemStore)
    this.discharge = new DirectionData(CarrierVisitDirection.Inbound, this, this.itemStore)
    this.restow = new JourneyCategoryData(JourneyCategory.Restow, this)
  }

  get vessels() {
    const data: IVesselItem[] = []
    if (!this.data.carrierIds || !this.vesselStore) return data

    for (const id of this.data.carrierIds) {
      const vessel = this.vesselStore.elements[id]
      if (vessel) data.push(vessel)
    }

    return data
  }

  get orders() {
    return _.get(this.itemStore.ordersByVesselVisitId, this.id) ?? []
  }

  get berths() {
    return _(this.data.berthIds ?? [])
      .map(berthId => this.berthStore?.elements[berthId])
      .compact()
      .map(item => item.data)
      .value()
  }

  // TODO: test
  get operator() {
    return this.data.vesselOperatorId
      ? this.companyStore?.elements[this.data.vesselOperatorId]
      : undefined
  }

  get shippingAgent() {
    return this.data.shippingAgentId
      ? this.companyStore?.elements[this.data.shippingAgentId]
      : undefined
  }

  get voyages() {
    return _(this.data.voyageNumbers ?? [])
      .map<VoyageDto>(v => {
        const shippingLine = v.shippingLineId
          ? this.companyStore?.elements[v.shippingLineId]
          : undefined

        return {
          id: v.id,
          inboundNumber: v.inboundVoyageNumber,
          outboundNumber: v.outboundVoyageNumber,
          shippingLine: shippingLine?.data,
        }
      })
      .value()
  }
  // End TODO: test

  get companies() {
    return _(this.data.voyageNumbers)
      .map(v => v.shippingLineId)
      .filter(Boolean)
      .map(id => this.companyStore?.elements[id!].data)
      .compact()
      .value()
  }

  get arrival() {
    return this.data.ata ?? this.data.eta
  }

  get departure() {
    return this.data.atd ?? this.data.etd
  }

  get status() {
    return getCarrierVisitViewStatus(this.data.status, this.data.atb)
  }

  get hasOpenOrders() {
    return this.orders.some(o => o.data.status === OrderStatus.Open)
  }

  get inboundOpenOrders() {
    return this.orders.filter(
      o =>
        o.data.status === OrderStatus.Open &&
        o.data.direction === CarrierVisitDirection.Inbound &&
        o.journeyCategory !== JourneyCategory.Restow,
    )
  }

  get outboundOpenOrders() {
    return this.orders.filter(
      o =>
        o.data.status === OrderStatus.Open &&
        o.data.direction === CarrierVisitDirection.Outbound &&
        o.journeyCategory !== JourneyCategory.Restow,
    )
  }

  get restowOpenOrders() {
    return this.orders.filter(
      o =>
        o.data.status === OrderStatus.Open &&
        o.data.direction === CarrierVisitDirection.Outbound &&
        o.journeyCategory === JourneyCategory.Restow,
    )
  }

  get hasDraftConflict() {
    const hasDraftConflict = this.berths.find(
      b =>
        b.maxDraft &&
        (b.maxDraft < (this.data.importDraft ?? 0) || b.maxDraft < (this.data.exportDraft ?? 0)),
    )

    return hasDraftConflict !== undefined
  }

  get isVesselVisitBlockedForEdit() {
    return isVisitStatusBlocked(this.data.status)
  }

  get isBerthed() {
    return this.data.atb != null
  }

  getCount = (
    countType: keyof IOrderCounts,
    direction: CarrierVisitDirection,
    cargoType: CargoType,
  ) => {
    return direction === CarrierVisitDirection.Inbound
      ? this.discharge.getCount(countType, cargoType)
      : this.load.getCount(countType, cargoType)
  }
}
